import React, { useMemo } from 'react';
import ImageFallback from 'react-image-fallback';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import API from '../../../../../../../service/api';
import {
    MDBCard,
    MDBIcon,
    MDBListGroup,
    MDBListGroupItem,
} from 'mdb-react-ui-kit';
import { ConfirmAction } from '../../../../../../../components/editable/ConfirmAction';
import { InputFieldEdit } from '../../../../../../../components/editable/InputFieldEdit';
import { StatusIcon } from '../../../../../../../components/StatusIcon';
import CashboxModal from '../../../../../../../components/modals/CashboxModal';
import { OpMachineDTO } from '@scm/common-legacy';

export interface MachineDetailsProps {
    machine: OpMachineDTO;
    apiURI: string;
    isConnected: boolean;
}
export const MachineDetails = ({
    apiURI,
    machine,
    isConnected,
}: MachineDetailsProps) => {
    const { t } = useTranslation();
    const serviceMode = useMemo(() => machine.serviceMode === true, [machine]);

    const notActivatedUntil = useMemo(() => {
        if (!machine.deliverAt) return null;
        const deliverAt = new Date(machine.deliverAt);
        return new Date(
            deliverAt.getFullYear() + 1,
            deliverAt.getMonth(),
            deliverAt.getDate(),
        );
    }, [machine.deliverAt]);

    // Permissions
    const canEditName = useMemo(
        () => machine?.permissions?.NameEdit,
        [machine],
    );
    const canEditCoinbox = useMemo(
        () => machine?.permissions?.MoneyBoxEdit && isConnected,
        [machine, isConnected],
    );
    const canClearMoneybox = useMemo(
        () => machine?.permissions?.ClearMoneyBox && isConnected,
        [machine, isConnected],
    );

    const StatusList = useMemo(() => {
        return (
            <React.Fragment>
                {machine.status.map((s) => (
                    <div key={s.id} style={{ display: 'inline' }}>
                        <ConfirmAction
                            title={t('clear_status')}
                            confirmText={t('clear')}
                            prompt={
                                <span>
                                    <Trans
                                        i18nKey={'clear_status_prompt'}
                                        values={{
                                            statusTitle: t(`status:${s.id}`),
                                        }}
                                    />
                                </span>
                            }
                            action={async () =>
                                await API.POST(`${apiURI}/statusclear`, {
                                    status: s.id,
                                })
                            }
                        >
                            {(open) => (
                                <StatusIcon
                                    badge
                                    name={s.id}
                                    ts={s.ts}
                                    onClick={open}
                                />
                            )}
                        </ConfirmAction>
                    </div>
                ))}
            </React.Fragment>
        );
    }, [machine.status]);

    if (!machine) return <div>...</div>;

    return (
        <section className="d-flex flex-column w-100 flex-fill justify-content-start mt-1 px-2 pb-2">
            {/* General */}
            <h5 className="ms-2">{t('general')}</h5>
            <MDBCard className="p-3">
                <MDBListGroup light className="p-0">
                    {machine.locked && (
                        <MDBListGroupItem
                            color="danger"
                            className="p-2 d-flex flex-row align-items-center"
                        >
                            <MDBIcon
                                icon="lock"
                                color="danger"
                                className="me-1"
                            />
                            <span>{t('machine:machine_locked')}</span>
                        </MDBListGroupItem>
                    )}
                    <MDBListGroupItem className="p-1 d-flex flex-row align-items-start">
                        <span className="fw-bold me-1">{t('name')}:</span>
                        <span className="d-flex flex-fill overflow-hidden">
                            {machine.name}
                        </span>
                        <InputFieldEdit
                            canEdit={canEditName}
                            type="text"
                            value={machine.name}
                            title={t('change_name')}
                            confirmText={t('change')}
                            inputLabel={t('name')}
                            action={async (name) =>
                                await API.PATCH(apiURI, { name })
                            }
                        />
                    </MDBListGroupItem>
                    <MDBListGroupItem className="p-1">
                        <span className="fw-bold me-1">
                            {t('type')}:
                            <ImageFallback
                                src={`/mtypes/mt${machine.type?.id ?? 0}.png`}
                                width={22}
                                height={22}
                                className="ms-1"
                                fallbackImage={<span></span>}
                            />
                        </span>
                        {machine.type?.name}
                    </MDBListGroupItem>
                    <MDBListGroupItem className="p-1">
                        <span
                            className="fw-bold me-1"
                            title={
                                machine.expireAt
                                    ? `${t('expire_at')} ${new Date(machine.expireAt).toLocaleDateString()}`
                                    : ''
                            }
                        >
                            {t('status_title')}:
                        </span>
                        {StatusList}
                        {machine.status.length == 0 && (
                            <span className="badge badge-primary ms-1">
                                {t('ok')}
                            </span>
                        )}
                    </MDBListGroupItem>

                    {serviceMode && (
                        <MDBListGroupItem className="p-1">
                            <span className="badge badge-info text-dark ms-1">
                                <i className="fas fa-wrench me-1" />
                                {t('machine:service_mode')}
                            </span>
                        </MDBListGroupItem>
                    )}

                    {!serviceMode &&
                        machine.expireAt &&
                        new Date(machine.expireAt).getTime() < Date.now() && (
                            <MDBListGroupItem className="p-1">
                                <span className="badge badge-danger ms-1">
                                    <i className="fas fa-calendar-xmark me-1" />
                                    {t('machine:machine_expired')}
                                </span>
                            </MDBListGroupItem>
                        )}

                    {!serviceMode &&
                        machine.expireAt &&
                        new Date(machine.expireAt).getTime() > Date.now() &&
                        new Date(machine.expireAt).getTime() - Date.now() <=
                            30 * 24 * 60 * 60 * 1000 && (
                            <MDBListGroupItem className="p-1">
                                <span className="badge badge-warning ms-1">
                                    <i className="fas fa-hourglass me-1" />
                                    {t('machine:machine_expire_1month')}
                                </span>
                            </MDBListGroupItem>
                        )}

                    {!serviceMode && machine.expireAt == null && (
                        <MDBListGroupItem className="p-1">
                            <span className="badge badge-warning ms-1">
                                <i className="fas fa-triangle-exclamation me-1" />
                                {t('machine:machine_not_activated_until')}{' '}
                                {notActivatedUntil
                                    ? notActivatedUntil.toLocaleDateString()
                                    : '??'}
                            </span>
                        </MDBListGroupItem>
                    )}
                </MDBListGroup>
            </MDBCard>

            {/* Description */}
            {machine.info && machine.info.length > 0 && (
                <>
                    <h5 className="ms-2 mt-2">{t('description')}</h5>
                    <div className="card p-3">
                        <span>{machine.info}</span>
                    </div>
                </>
            )}

            {/* Aditional */}
            {machine.aditional && Object.keys(machine.aditional).length > 0 && (
                <>
                    <h5 className="ms-2 mt-4">{t('machine:aditional')}</h5>
                    <MDBCard className="p-1">
                        <MDBListGroupItem className="p-1">
                            {Object.keys(machine.aditional).map((key) => (
                                <span
                                    key={key}
                                    className="badge badge-primary text-dark ms-1"
                                >
                                    {key}:{' '}
                                    <b className="text-primary">
                                        {
                                            (
                                                machine.aditional as {
                                                    [key: string]: string;
                                                }
                                            )[key]
                                        }
                                    </b>
                                </span>
                            ))}
                        </MDBListGroupItem>
                    </MDBCard>
                </>
            )}

            {/* Identification */}
            <h5 className="ms-2 mt-4">{t('machine:identification')}</h5>
            <MDBCard className="p-2">
                <MDBListGroup light className="p-0">
                    <MDBListGroupItem className="p-1">
                        <MDBIcon
                            icon="sim-card"
                            fixed
                            color="primary"
                            className="me-1"
                        />
                        <span className="fw-bold me-1">{t('machine:sn')}:</span>
                        {machine.sn}
                    </MDBListGroupItem>
                    <MDBListGroupItem className="p-1">
                        <MDBIcon
                            icon="memory"
                            fixed
                            color="primary"
                            className="me-1"
                        />
                        <span className="fw-bold me-1">
                            {t('machine:imei')}:
                        </span>
                        {machine.IMEI}
                    </MDBListGroupItem>
                    <MDBListGroupItem className="p-1">
                        <MDBIcon
                            icon="satellite"
                            fixed
                            color="primary"
                            className="me-1"
                        />
                        <span className="fw-bold me-1">
                            {t('machine:iccid')}:
                        </span>
                        {machine.ICCID}
                    </MDBListGroupItem>
                </MDBListGroup>
            </MDBCard>

            {/* Balance */}
            <div className="d-flex flex-row ms-2 mt-4 mb-2 align-items-center">
                <h5 className="mb-0">{t('machine:balance')}</h5>
            </div>
            <MDBCard className="p-2">
                <MDBListGroup light className="p-0">
                    {machine.coins !== undefined && (
                        <MDBListGroupItem className="d-flex flex-row align-items-center p-1">
                            <span className="d-inline-block me-3">
                                <MDBIcon
                                    icon="coins"
                                    fixed
                                    color="primary"
                                    className="me-1"
                                />
                                <span className="fw-bold me-1">
                                    {machine.type?.id == 3
                                        ? t('machine:cashbox_banknotes')
                                        : t('machine:cashbox_coins')}
                                    :
                                </span>
                                {machine.coins}
                            </span>
                            <span className="flex-fill d-flex justify-content-end ms-2">
                                <InputFieldEdit
                                    action={async (coins) =>
                                        await API.PATCH(apiURI, {
                                            coinBoxNr: Number(coins),
                                        })
                                    }
                                    title={
                                        machine.type?.id == 3
                                            ? t('machine:edit_banknotes')
                                            : t('machine:edit_coins')
                                    }
                                    confirmText={t('update')}
                                    canEdit={canEditCoinbox}
                                    value={machine.coins}
                                    type="number"
                                    inputLabel={
                                        machine.type?.id == 3
                                            ? t('machine:cashbox_banknotes')
                                            : t('machine:cashbox_coins')
                                    }
                                />

                                {canClearMoneybox && (
                                    <ConfirmAction
                                        title={t('machine:clear_cashbox')}
                                        confirmText={t('clear')}
                                        action={async () =>
                                            await API.POST(
                                                `${apiURI}/clearmoneybox`,
                                            )
                                        }
                                        prompt={t(
                                            'machine:clear_cashbox_prompt',
                                        )}
                                    >
                                        {(open) => (
                                            <span
                                                role="button"
                                                onClick={() => open()}
                                            >
                                                <MDBIcon
                                                    icon="backspace"
                                                    color="primary"
                                                    size="lg"
                                                    className="ms-1"
                                                />
                                            </span>
                                        )}
                                    </ConfirmAction>
                                )}

                                {canClearMoneybox && machine.type?.id !== 3 && (
                                    <ConfirmAction
                                        title={t('machine:operate')}
                                        confirmText={t('confirm')}
                                        action={async () =>
                                            await API.POST(`${apiURI}/operate`)
                                        }
                                        prompt={t('machine:operate_prompt')}
                                    >
                                        {(open) => (
                                            <span
                                                role="button"
                                                onClick={() => open()}
                                            >
                                                <MDBIcon
                                                    icon="piggy-bank"
                                                    color="primary"
                                                    size="lg"
                                                    className="ms-1"
                                                />
                                            </span>
                                        )}
                                    </ConfirmAction>
                                )}

                                <CashboxModal
                                    machine={machine}
                                    apiURI={apiURI}
                                />
                            </span>
                        </MDBListGroupItem>
                    )}
                    {(machine.topCnt !== undefined ||
                        machine.botCnt !== undefined) && (
                        <MDBListGroupItem className="p-1">
                            <MDBIcon
                                icon="tachograph-digital"
                                fixed
                                color="primary"
                                className="me-1"
                            />
                            <span className="fw-bold me-1">
                                {t('machine:counters')}:
                            </span>
                            {machine.topCnt !== undefined && (
                                <>
                                    <MDBIcon
                                        icon="caret-up"
                                        color="secondary"
                                    />
                                    {machine.topCnt}
                                </>
                            )}
                            {machine.topCnt !== undefined &&
                                machine.botCnt !== undefined && <span>/</span>}
                            {machine.botCnt !== undefined && (
                                <>
                                    <MDBIcon
                                        icon="caret-down"
                                        color="secondary"
                                    />
                                    {machine.botCnt}
                                </>
                            )}
                        </MDBListGroupItem>
                    )}
                    {machine.value !== undefined && (
                        <MDBListGroupItem className="d-flex flex-row align-items-center p-1 list-group-item-success">
                            <MDBIcon
                                icon="hand-holding-dollar"
                                fixed
                                color="primary"
                                className="me-1"
                            />
                            <span className="fw-bold me-1">{t('value')}:</span>
                            {machine.value}
                            <span className="flex-fill d-flex justify-content-end ms-2">
                                <InputFieldEdit
                                    action={async (value) =>
                                        await API.PATCH(apiURI, {
                                            coinBoxVal: Number(value),
                                        })
                                    }
                                    title={t('edit_value')}
                                    confirmText={t('update')}
                                    canEdit={canEditCoinbox}
                                    value={machine.value}
                                    type="number"
                                    inputLabel={t('value')}
                                    inputTransformation={(value) =>
                                        machine.cUnit *
                                        Math.round(
                                            (value as number) / machine.cUnit,
                                        )
                                    }
                                />
                            </span>
                        </MDBListGroupItem>
                    )}
                </MDBListGroup>
            </MDBCard>
        </section>
    );
};
